import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquareText, Plus, Send, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { analytics } from "@/lib/analytics";
import { fmtDateTime } from "@/lib/format";

const DAYS = ["S", "M", "T", "W", "T", "F", "S"];
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const ZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu", "UTC", "Europe/London"];

export const formatPhone = (n) => {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(n);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : n;
};

export const normalizePhone = (raw) => {
  const digits = raw.replace(/[^\d+]/g, "");
  if (/^\+[1-9]\d{7,14}$/.test(digits)) return digits;
  const d = digits.replace(/\D/g, "");
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  return null;
};

export const PhoneChips = ({ phones, onChange }) => {
  const [text, setText] = useState("");
  const add = () => {
    const parts = text.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...phones];
    for (const p of parts) {
      const n = normalizePhone(p);
      if (!n) { toast.error(`"${p}" isn't a valid phone number — use 10 digits or +1…`); continue; }
      if (next.some((x) => x.number === n)) { toast.message(`${formatPhone(n)} is already on the list`); continue; }
      next.push({ number: n, label: "", enabled: true });
    }
    onChange(next); setText("");
  };
  return (
    <div className="tc-chipbox" data-testid="phone-chips" onClick={(e) => e.currentTarget.querySelector(".tc-chipbox__input")?.focus()}>
      {phones.map((p) => (
        <span key={p.number} className={`tc-phonechip${p.enabled === false ? " is-off" : ""}`} data-testid={`phone-chip-${p.number}`}>
          <button type="button" className="tc-phonechip__num" title={p.enabled === false ? "Paused — click to resume" : "Click to pause this number"} onClick={() => onChange(phones.map((x) => (x.number === p.number ? { ...x, enabled: x.enabled === false } : x)))} data-testid={`phone-toggle-${p.number}`}>{formatPhone(p.number)}</button>
          <input className="tc-phonechip__label" placeholder="name" value={p.label || ""} onChange={(e) => onChange(phones.map((x) => (x.number === p.number ? { ...x, label: e.target.value.slice(0, 40) } : x)))} data-testid={`phone-label-${p.number}`} />
          <button type="button" className="tc-phonechip__x" title="Remove" onClick={() => onChange(phones.filter((x) => x.number !== p.number))} data-testid={`phone-remove-${p.number}`}><X size={12} /></button>
        </span>
      ))}
      <input className="tc-chipbox__input" value={text} placeholder={phones.length ? "Add another number…" : "Type a mobile number and press Enter (e.g. 706 555 0123)"} onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } if (e.key === "Backspace" && !text && phones.length) onChange(phones.slice(0, -1)); }}
        onBlur={add} onPaste={(e) => { e.preventDefault(); setText((t) => t + e.clipboardData.getData("text")); setTimeout(add, 0); }} data-testid="phone-input" />
    </div>
  );
};

const WindowRow = ({ w, onChange, onRemove, idx }) => (
  <div className="tc-window" data-testid={`alert-window-${idx}`}>
    <div className="tc-window__days">
      {DAYS.map((d, i) => <button key={i} type="button" className={`tc-daychip${w.days.includes(i) ? " is-on" : ""}`} title={DAY_NAMES[i]} onClick={() => onChange({ ...w, days: w.days.includes(i) ? w.days.filter((x) => x !== i) : [...w.days, i].sort() })} data-testid={`window-${idx}-day-${i}`}>{d}</button>)}
    </div>
    <input type="time" className="tc-input sm" value={w.start} onChange={(e) => onChange({ ...w, start: e.target.value })} data-testid={`window-${idx}-start`} />
    <span className="muted">to</span>
    <input type="time" className="tc-input sm" value={w.end} onChange={(e) => onChange({ ...w, end: e.target.value })} data-testid={`window-${idx}-end`} />
    <button type="button" className="tc-iconbtn" title="Remove window" onClick={onRemove} data-testid={`window-${idx}-remove`}><X size={14} /></button>
  </div>
);

export const HealthAlerts = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [log, setLog] = useState([]);
  const [testTo, setTestTo] = useState("");
  const loadLog = () => api.get("/admin/health/sms-log", { params: { limit: 30 } }).then((r) => setLog(r.data.messages)).catch(() => {});
  useEffect(() => { api.get("/admin/health/alerts").then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e))); loadLog(); }, []);
  const set = (patch) => { setCfg((c) => ({ ...c, ...patch })); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/health/alerts", { sms_enabled: cfg.sms_enabled, phones: cfg.phones, windows: cfg.windows, timezone: cfg.timezone, consecutive: Number(cfg.consecutive), realert_minutes: Number(cfg.realert_minutes), recovery: cfg.recovery, from_number: cfg.from_number });
      setCfg(data); setDirty(false); toast.success("Alert settings saved");
      analytics.track("health_alerts_saved", { category: "health", metadata: { phones: data.phones.length, windows: data.windows.length } });
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const quick = async (patch) => {
    try { const { data } = await api.patch("/admin/health/alerts", patch); setCfg((c) => ({ ...data, ...(dirty ? { phones: c.phones, windows: c.windows } : {}) })); } catch (e) { toast.error(errMsg(e)); }
  };
  const sendTest = async () => {
    if (dirty) { toast.message("Save your changes first so the test uses the new recipients"); return; }
    try {
      const { data } = await api.post("/admin/health/alerts/test", testTo ? { to: testTo } : {});
      toast.success(`Test SMS sent to ${data.to.map(formatPhone).join(", ")}`); setTestTo(""); loadLog();
    } catch (e) { toast.error(errMsg(e)); loadLog(); }
  };
  if (!cfg) return <p className="muted" style={{ padding: 24 }}>Loading…</p>;
  const active = cfg.phones.filter((p) => p.enabled !== false).length;
  return (
    <div data-testid="health-alerts">
      {!cfg.configured && <div className="tc-callout tc-callout--warn" data-testid="sms-not-configured">OpenPhone isn't connected. Add <code>OPENPHONE_API_KEY</code> and <code>OPENPHONE_FROM_NUMBER</code> to the backend secrets to enable SMS alerts.</div>}
      <div className="tc-admin-card">
        <div className="tc-admin-card__head">SMS alerts via OpenPhone
          <span className="right" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="muted" style={{ fontSize: 12 }}>{cfg.sms_enabled ? "On" : "Paused"}</span>
            <Switch checked={!!cfg.sms_enabled} onCheckedChange={(v) => { setCfg((c) => ({ ...c, sms_enabled: v })); quick({ sms_enabled: v }); }} data-testid="sms-enabled-switch" />
          </span>
        </div>
        <div className="tc-admin-card__body tc-form">
          <div className="tc-field">
            <span className="tc-field__label">Alert recipients <span className="muted">— {active} active</span></span>
            <PhoneChips phones={cfg.phones} onChange={(phones) => set({ phones })} />
            <span className="tc-field__hint">Every number gets every alert. Click a number to pause it, add an optional name, press Backspace to remove the last one.</span>
          </div>
          <label className="tc-field">
            <span className="tc-field__label">Send from</span>
            <select className="tc-select" value={cfg.from_number || ""} onChange={(e) => set({ from_number: e.target.value })} data-testid="from-number-select">
              {!cfg.numbers.some((n) => n.number === cfg.from_number) && <option value={cfg.from_number || ""}>{cfg.from_number || "Select a number"}</option>}
              {cfg.numbers.map((n) => <option key={n.id} value={n.number}>{formatPhone(n.number)}{n.name ? ` — ${n.name}` : ""}</option>)}
            </select>
            <span className="tc-field__hint">Your OpenPhone numbers. The default comes from <code>OPENPHONE_FROM_NUMBER</code>.</span>
          </label>
        </div>
      </div>

      <div className="tc-admin-card">
        <div className="tc-admin-card__head">Alert windows<span className="right muted" style={{ fontSize: 12 }} data-testid="window-now">{cfg.in_window ? "Alerts would send right now" : "Outside window — alerts are held"} · {cfg.local_time}</span></div>
        <div className="tc-admin-card__body tc-form">
          <p className="muted" style={{ margin: 0 }}>Texts only go out inside these windows. Leave empty for 24/7. An outage that starts outside a window is texted as soon as the next window opens (if it's still ongoing).</p>
          {cfg.windows.map((w, i) => <WindowRow key={i} idx={i} w={w} onChange={(nw) => set({ windows: cfg.windows.map((x, j) => (j === i ? nw : x)) })} onRemove={() => set({ windows: cfg.windows.filter((_, j) => j !== i) })} />)}
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <button type="button" className="tc-btn tc-btn--outline sm" onClick={() => set({ windows: [...cfg.windows, { days: [1, 2, 3, 4, 5], start: "08:00", end: "18:00" }] })} data-testid="add-window"><Plus size={13} /> Add window</button>
            <label className="tc-field" style={{ flexDirection: "row", alignItems: "center", gap: 8, margin: 0 }}>
              <span className="muted">Timezone</span>
              <select className="tc-select sm" value={cfg.timezone} onChange={(e) => set({ timezone: e.target.value })} data-testid="timezone-select">{[...new Set([cfg.timezone, ...ZONES])].map((z) => <option key={z}>{z}</option>)}</select>
            </label>
          </div>
        </div>
      </div>

      <div className="tc-admin-card">
        <div className="tc-admin-card__head">Rules</div>
        <div className="tc-admin-card__body tc-rules">
          <label className="tc-rule"><span>Alert after</span><input type="number" min={1} max={10} className="tc-input sm" style={{ width: 64 }} value={cfg.consecutive} onChange={(e) => set({ consecutive: e.target.value })} data-testid="consecutive-input" /><span>consecutive failed checks (~{cfg.consecutive} min)</span></label>
          <label className="tc-rule"><span>Re-alert every</span><input type="number" min={5} max={1440} step={5} className="tc-input sm" style={{ width: 72 }} value={cfg.realert_minutes} onChange={(e) => set({ realert_minutes: e.target.value })} data-testid="realert-input" /><span>minutes while a component is still <strong>down</strong> (degraded states are texted once, and again if they escalate)</span></label>
          <label className="tc-rule"><Switch checked={!!cfg.recovery} onCheckedChange={(v) => set({ recovery: v })} data-testid="recovery-switch" /><span>Text when a component recovers</span></label>
        </div>
      </div>

      <div className="tc-admin__toolbar">
        <button className="tc-btn tc-btn--primary" disabled={!dirty || saving} onClick={save} data-testid="alerts-save">{saving ? "Saving…" : dirty ? "Save changes" : "Saved"}</button>
        <span className="grow" />
        <input className="tc-input sm" style={{ width: 200 }} placeholder="Test number (optional)" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="test-sms-to" />
        <button className="tc-btn tc-btn--outline sm" disabled={!cfg.configured} onClick={sendTest} data-testid="send-test-sms"><Send size={13} /> Send test SMS{!testTo && active ? ` to ${active}` : ""}</button>
      </div>

      <div className="tc-admin-card">
        <div className="tc-admin-card__head"><MessageSquareText size={14} style={{ display: "inline", verticalAlign: -2 }} /> SMS log<span className="right"><button className="tc-linkbtn" onClick={loadLog}>Refresh</button></span></div>
        <table className="tc-table" data-testid="sms-log">
          <thead><tr><th>When</th><th>Type</th><th>To</th><th>Status</th><th>Message</th></tr></thead>
          <tbody>
            {log.map((m) => (
              <tr key={m.id} data-testid={`sms-row-${m.id}`}>
                <td className="muted" style={{ whiteSpace: "nowrap" }}>{fmtDateTime(m.ts)}</td>
                <td><span className={`tc-pill ${m.kind === "alert" ? "danger" : m.kind === "recovery" ? "server" : ""}`}>{m.kind}</span></td>
                <td className="muted">{(m.to || []).map(formatPhone).join(", ")}</td>
                <td><span className={`tc-pill ${m.status === "sent" ? "server" : m.status === "failed" ? "danger" : ""}`} title={m.error || ""}>{m.status}</span>{m.error && <div className="muted" style={{ fontSize: 11, maxWidth: 220 }}>{m.error}</div>}</td>
                <td><pre className="tc-smsbody">{m.body}</pre></td>
              </tr>
            ))}
            {!log.length && <tr><td colSpan={5} className="muted" style={{ textAlign: "center", padding: 24 }}>No SMS sent yet. Use "Send test SMS" to verify delivery.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
